import { ElementRef, Injectable, inject } from '@angular/core';
import { LoggerService } from '@shared/services/logger.service';
import { WhiteboardUpdate } from '@osc-utils/models/whiteboard-update';
import { DrawingOptions } from '../interfaces/drawing-options';

@Injectable({
  providedIn: 'root',
})
export class CanvasService {
  #logger = inject(LoggerService);

  #shapesMap = new Map<string, FreeHandShape>();
  #context: CanvasRenderingContext2D;

  public getCanvas(
    canvas: ElementRef | HTMLCanvasElement,
    height: number,
    width: number,
    historicalDraw: WhiteboardUpdate[],
    drawingOptions: DrawingOptions,
    backgroundImage?: HTMLImageElement,
    includeBackgroundImage = true,
  ): void {
    this.#shapesMap = new Map();
    this.#context = canvas instanceof ElementRef ? canvas.nativeElement.getContext('2d') : canvas.getContext('2d');
    if (backgroundImage) {
      this.#context.canvas.width = backgroundImage.naturalWidth;
      this.#context.canvas.height = backgroundImage.naturalHeight;
      this.#context.scale(backgroundImage.naturalWidth / width, backgroundImage.naturalHeight / height);
      if (includeBackgroundImage) {
        this.#context.drawImage(backgroundImage, 0, 0, width, height);
      }
      this.#context.setTransform(1, 0, 0, 1, 0, 0);
    } else {
      this.#context.canvas.width = width;
      this.#context.canvas.height = height;
    }
    this.#context.lineWidth = drawingOptions.lineWidth;
    this.#context.fillStyle = `rgba(255, 0, 0, 1)`;
    this.#context.strokeStyle = 'rgba(255, 0, 0, 1)';
    if (historicalDraw) {
      historicalDraw.forEach(item => {
        this.draw(item, drawingOptions);
      });
    } else {
      this.#logger.info('undefined redline data passed.');
    }
  }

  public draw(item: WhiteboardUpdate, drawingOptions: DrawingOptions) {
    if (item.type === 0) {
      const shape = new FreeHandShape(item.x * this.#context.canvas.width, item.y * this.#context.canvas.height, drawingOptions);
      this.#shapesMap.set(item.UUID, shape);
    } else if (item.type === 1) {
      const shape = this.#shapesMap.get(item.UUID);
      shape.onUpdateReceived(item, this.#context);
    } else if (item.type === 2) {
      const shape = this.#shapesMap.get(item.UUID);
      this.#shapesMap.set(item.UUID, shape);
      shape.draw(this.#context);
    }
  }
}

class FreeHandShape {
  public linePositions: { x: number; y: number }[];
  public constructor(
    public x: number,
    public y: number,
    public options: DrawingOptions,
  ) {
    this.linePositions = [];
  }

  public draw(context: CanvasRenderingContext2D) {
    context.beginPath();
    context.moveTo(this.x, this.y);
    context.lineTo(this.x + 1, this.y + 1);
    let i = 0;
    while (i < this.linePositions.length) {
      if (this.linePositions.length - i > 2) {
        const controlPoint1 = this.linePositions[i];
        const controlPoint2 = this.linePositions[i + 1];
        const endPoint = this.linePositions[i + 2];
        context.bezierCurveTo(controlPoint1.x, controlPoint1.y, controlPoint2.x, controlPoint2.y, endPoint.x, endPoint.y);
        i += 2;
      } else {
        const linePosition = this.linePositions[i];
        context.lineTo(linePosition.x, linePosition.y);
        i += 1;
      }
    }
    context.stroke();
  }

  public onUpdateReceived(update: WhiteboardUpdate, context: CanvasRenderingContext2D) {
    this.linePositions.push({ x: update.x * context.canvas.width, y: update.y * context.canvas.height });
  }
}
